import { useEffect, useRef } from "react";
import axios from "axios";
import authStore from "../utils/authStorage";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const IDLE_TIMEOUT_MS = 60 * 60 * 1000;
const REFRESH_CHECK_MS = 60 * 1000;
const MIN_REFRESH_GAP_MS = 10 * 60 * 1000;
const ACTIVITY_WRITE_GAP_MS = 1000;

const ACTIVITY_EVENTS = [
  "click",
  "keydown",
  "mousemove",
  "mousedown",
  "scroll",
  "touchstart",
  "input",
  "change",
];

const now = () => Date.now();

const getLastActivityAt = () =>
  Number(localStorage.getItem("lastActivityAt") || now());

const hasActiveSession = () =>
  Boolean(localStorage.getItem("token") && localStorage.getItem("userId"));

export default function SessionManager() {
  const lastActivityWriteRef = useRef(0);
  const lastRefreshRef = useRef(0);
  const refreshInFlightRef = useRef(false);

  useEffect(() => {
    const markActive = () => {
      if (!hasActiveSession()) return;
      const currentTime = now();
      if (currentTime - getLastActivityAt() >= IDLE_TIMEOUT_MS) { endIdleSession(); return; }
      if (currentTime - lastActivityWriteRef.current < ACTIVITY_WRITE_GAP_MS) {
        return;
      }
      lastActivityWriteRef.current = currentTime;
      localStorage.setItem("lastActivityAt", String(currentTime));
    };

    const endIdleSession = () => {
      authStore.clearAll();
      alert("Session ended after being idle. Please log in again.");
      if (window.location.pathname !== "/") {
        window.location.assign("/");
      }
    };

    const refreshSession = async () => {
      if (!hasActiveSession() || refreshInFlightRef.current) return;

      const currentTime = now();
      const idleForMs = currentTime - getLastActivityAt();
      if (idleForMs >= IDLE_TIMEOUT_MS) {
        endIdleSession();
        return;
      }

      if (currentTime - lastRefreshRef.current < MIN_REFRESH_GAP_MS) return;

      refreshInFlightRef.current = true;
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");
      try {
        const response = await axios.post(
          `${API_BASE}/api/session/refresh`,
          {},
          { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 },
        );
        if (localStorage.getItem("token") !== token || localStorage.getItem("userId") !== userId) return;
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("userId", response.data.id);
        localStorage.setItem("userRole", String(response.data.role));
        axios.defaults.headers.common.Authorization = `Bearer ${response.data.token}`;
        lastRefreshRef.current = currentTime;
        window.dispatchEvent(new Event("authTokenRefreshed"));
      } catch {
        // Keep forms and active work intact; retry on the next check.
        // The backend still validates authentication for every API request.
      } finally {
        refreshInFlightRef.current = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshSession();
      }
    };

    if (hasActiveSession() && !localStorage.getItem("lastActivityAt")) {
      markActive();
    }

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, markActive, { passive: true });
    });
    document.addEventListener("visibilitychange", handleVisibilityChange);
    const timer = window.setInterval(refreshSession, REFRESH_CHECK_MS);
    refreshSession();

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, markActive);
      });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearInterval(timer);
    };
  }, []);

  return null;
}
